import { Inject, Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { TimesheetModel, TimesheetModelFromJson } from '../../models/low-level/timesheet.model';
import { catchError, map, mergeMap, take } from 'rxjs/operators';
import { TIMESHEET_KEYS } from '../../enums/timesheet.enum';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { AppNotificationService, AppNotificationType, ConcurrentUsageService, isDefined } from '@evorait/pace-core';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class StorageTimesheetService {
    public constructor(
        private readonly concurrencyStore: ConcurrentUsageService,
        private readonly appNotificationService: AppNotificationService,
        private readonly translate: TranslateService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {}

    public getTimesheet$(storageId: TIMESHEET_KEYS): Observable<TimesheetModel | undefined> {
        return this.concurrencyStore.liveQueryDataForKey$(storageId).pipe(
            map((result) => {
                if (!isDefined(result)) {
                    this.log.warn(`StorageTimesheetService: getTimesheet: No data found for key ${storageId}`);
                    return result;
                }
                const timesheet = new TimesheetModelFromJson(JSON.parse(result.value), storageId);
                if (timesheet.saveTimestamp !== result.timestamp) {
                    timesheet.saveTimestamp = result.timestamp;
                }
                return timesheet;
            }),
            catchError((error) => {
                const errorMessage = `StorageTimesheetService: getTimesheet: ERROR Reading timesheet "${storageId}" failed: ${error}`;
                this.log.error(errorMessage);
                let timesheetType = '';
                switch (storageId) {
                    case TIMESHEET_KEYS.CURRENT_TIMESHEET:
                        timesheetType = 'current';
                        break;
                    case TIMESHEET_KEYS.SUBMITTED_TIMESHEET:
                        timesheetType = 'submitted';
                        break;
                    default:
                        timesheetType = '';
                        break;
                }
                this.appNotificationService.setAppNotification(
                    AppNotificationType.ERROR,
                    <string>this.translate.instant('TIMESHEET_GET_FROM_STORAGE_ERROR_TITLE'),
                    <string>(
                        this.translate.instant('TIMESHEET_GET_FROM_STORAGE_ERROR_BODY', { timesheetType, errorMessage })
                    ),
                );
                return throwError(() => new Error(errorMessage));
            }),
        );
    }

    public saveTimesheet$(timesheetKey: string, data: TimesheetModel): Observable<TimesheetModel> {
        const stringifiedData = JSON.stringify(data);
        const notifs = data.notifications.map(
            (notif) => `${notif.notificationId.toString()}: is confirmation submitted ${notif.isConfirmationSubmitted}`,
        );
        this.log.debug(
            `StorageTimesheetService: saveTimesheet: Storing concurrent usage data for ${timesheetKey} \n`,
            notifs.join('\n'),
        );
        return this.concurrencyStore.writeDataForKey(timesheetKey, stringifiedData, data.saveTimestamp).pipe(
            map(() => data),
            catchError((error) => {
                this.log.error(
                    `StorageTimesheetService: saveTimesheet: Saving timesheet "${timesheetKey}" failed: ${error}`,
                );
                return of(data);
            }),
        );
    }

    /**
     * Checks if the timesheet object was changed before writing it to indexeddb and concurrency store.
     *
     * @returns Observable<TimesheetModel>
     * @param key type of timesheet, either CURRENT_TIMESHEET or SUBMITTED_TIMESHEET
     * @param timesheet timesheet object to be written
     */
    public saveDistinctTimesheet$(key: TIMESHEET_KEYS, timesheet: TimesheetModel): Observable<TimesheetModel> {
        return this.getTimesheet$(key).pipe(
            take(1),
            mergeMap((localTimesheet) => {
                if (isDefined(localTimesheet)) {
                    const stringifiedLocalTimesheet = JSON.stringify(localTimesheet);
                    const stringifiedNewTimesheet = JSON.stringify(timesheet);

                    // if the timesheet was not saved compared to the last save, then we skip saving it to avoid triggering
                    // an update loop from indexeddb livequery
                    if (stringifiedLocalTimesheet === stringifiedNewTimesheet) {
                        return of(localTimesheet);
                    }
                }

                timesheet.saveTimestamp = Date.now();
                return this.saveTimesheet$(key, timesheet);
            }),
        );
    }
}
